import React from "react";
import { Document, Page, pdf } from "@react-pdf/renderer";
import { PAGE } from "./layout/constants";
import { chunkIntoPages, slotForIndex, perPageFor } from "./sheetComposer";
import { artSlotForIndex } from "./layout/artGeometryPt";
import { generateQrDataUrl } from "./qrCode";
import LabelTagPdf from "./LabelTagPdf";
import { composeSheetWithUnits } from "./composeSheetWithUnits";

/** Blob.arrayBuffer não existe no jsdom do CRA; FileReader cobre os dois lados. */
const blobToArrayBuffer = (blob) => {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

const attachQrDataUrls = async (cards) =>
  Promise.all(
    cards.map(async (card) => ({
      ...card,
      qrDataUrl: card.qrDataUrl || (await generateQrDataUrl(card.serial)),
    }))
  );

/**
 * Overlay transparente com texto + QR. Moldura vem das unidades carimbadas
 * em composeSheetWithUnits — só slots ocupados recebem arte.
 *
 * Papel "art": grade de 8 por página (perPageFor) com o slot de artSlotForIndex
 * (retângulo { x, y, width, height } em coordenadas de topo) e `artGeometry`
 * injetado em cada carta — a Task 7 lê `card.artGeometry` quando
 * `card.visual === "art_extended"`. Até lá o LabelTagPdf desenha essas cartas
 * com o layout legado (esperado; a moldura/PNG real vem de composeSheetWithUnits).
 */
export function PaperDocument({ cards, paper = "majorca", geometry }) {
  const pages = chunkIntoPages(cards, perPageFor(paper));
  return (
    <Document>
      {pages.map((pageCards, pageIndex) => (
        <Page
          key={`page-${pageIndex}`}
          size={[PAGE.width, PAGE.height]}
          style={{ position: "relative" }}
        >
          {pageCards.map((card, indexOnPage) => {
            const slot =
              paper === "art" ? artSlotForIndex(indexOnPage, geometry) : slotForIndex(indexOnPage);
            const cardForPdf = paper === "art" ? { ...card, artGeometry: geometry } : card;
            return (
              <LabelTagPdf
                key={card.serial || `slot-${indexOnPage}`}
                card={cardForPdf}
                slot={slot}
              />
            );
          })}
        </Page>
      ))}
    </Document>
  );
}

/**
 * PDF multipage de um papel de estoque (dourado / majorca / colorido / art).
 * Cada carta carimba a unidade do seu visual; molduras vazias não aparecem.
 */
export async function renderPaperPdfBlob(cards, { paper = "majorca", geometry, resolveArtUrl } = {}) {
  const cardsWithQr = await attachQrDataUrls(cards);
  const overlayBlob = await pdf(
    <PaperDocument cards={cardsWithQr} paper={paper} geometry={geometry} />
  ).toBlob();
  const overlayBytes = await blobToArrayBuffer(overlayBlob);
  const merged = await composeSheetWithUnits(overlayBytes, cardsWithQr, {
    paper,
    geometry,
    resolveArtUrl,
  });
  return new Blob([merged], { type: "application/pdf" });
}

/** @deprecated alias — testes antigos; preferir renderPaperPdfBlob */
export async function renderBucketPdfBlob(cards, _bucket) {
  return renderPaperPdfBlob(cards);
}

export { attachQrDataUrls };
